import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { App } from 'antd'
import { useTranslation } from 'react-i18next'
import { api, money, type Product } from '../api'
import { useSession } from '../session'

/**
 * What the insurer sells. The chain holds what a policy is bound by — the
 * insurer, the cover limit, the term, the refund rule — and the registry holds
 * what an underwriter may change freely: the name, the description and the
 * price. A policy keeps the terms it was sold under either way.
 */
export default function Products() {
  const { t } = useTranslation()
  const products = useQuery({ queryKey: ['products'], queryFn: api.products })
  const [editing, setEditing] = useState<Product | null>(null)
  const [tariff, setTariff] = useState<Product | null>(null)

  return (
    <div className="page page-mid stack">
      <div className="panel">
        <div className="panel-head">
          <span className="grow">{t('products')}</span>
          <button className="btn-small" onClick={() => setEditing({} as Product)}>{t('defineProduct')}</button>
        </div>
        <div className="panel-body flush">
          {products.isLoading && <div className="panel-body"><div className="skeleton" style={{ height: 60 }} /></div>}
          {!products.isLoading && (products.data?.data.length ?? 0) === 0 && <p className="empty">{t('noData')}</p>}
          {(products.data?.data.length ?? 0) > 0 && (
            <div className="scroll-x">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('code')}</th><th>{t('kindLabel')}</th><th>{t('name')}</th><th>{t('insurerAccount')}</th>
                    <th className="num">{t('coverageLimit')}</th><th className="num">{t('termDays')}</th>
                    <th className="num">{t('rateBps')}</th><th>{t('refundMode')}</th><th>{t('status')}</th><th />
                  </tr>
                </thead>
                <tbody>
                  {products.data!.data.map(p => (
                    <tr key={p.code} className="clickable" onClick={() => setEditing(p)}>
                      <td className="mono">{p.code}</td>
                      <td>{p.kind === 'bond' ? t('kindBond') : t('kindCover')}</td>
                      <td>{p.name}</td>
                      <td>{p.insurer ?? '—'}</td>
                      <td className="num">{money(p.coverage_limit)}</td>
                      <td className="num">{p.term_days ?? '—'}</td>
                      <td className="num">{p.rate_bps ?? '—'}</td>
                      <td>{p.refund === 'pro_rata' ? t('refundProRata') : p.refund ? t('refundNone') : '—'}</td>
                      <td><span className={`state ${p.active ? 'active' : 'expired'}`}>
                        {p.active ? t('onSale') : t('expired')}</span></td>
                      <td>
                        <button type="button" className="btn-ghost"
                          onClick={e => { e.stopPropagation(); setTariff(p) }}>{t('tariff')}</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {editing && <ProductForm product={editing} onDone={() => setEditing(null)} />}
      {tariff && <TariffForm product={tariff} onDone={() => setTariff(null)} />}
      <ReferralCodes products={products.data?.data ?? []} />
      <Fund />
    </div>
  )
}

/**
 * Settlement money onto the ledger. Only an issuer sees this: STABLE is what
 * policies are paid in and claims are paid out of, and until the corporate
 * gateway exists it is put there by hand against a transfer that has cleared.
 */
function Fund() {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const qc = useQueryClient()
  const { me } = useSession()
  const [account, setAccount] = useState('')
  const [amount, setAmount] = useState('')
  const [pin, setPin] = useState('')

  const mint = useMutation({
    mutationFn: () => api.mint({ account: account.trim(), amount: Number(amount), pin: pin || undefined }),
    onSuccess: () => {
      message.success(t('minted'))
      setAmount(''); setPin('')
      qc.invalidateQueries({ queryKey: ['me'] })
      qc.invalidateQueries({ queryKey: ['balances'] })
    },
    onError: (e: Error) => message.error(e.message),
  })

  if (!me?.staff?.issuer) return null

  const submit = (e: FormEvent) => { e.preventDefault(); mint.mutate() }
  const ready = account.trim().length > 0 && Number(amount) > 0

  return (
    <form className="panel" onSubmit={submit}>
      <div className="panel-head"><span className="grow">{t('fundAccount')}</span></div>
      <div className="panel-body stack">
        <div className="cards">
          <div className="field">
            <label htmlFor="fund-account">{t('account')}</label>
            <div className="input-wrap">
              <input id="fund-account" value={account} autoComplete="off"
                onChange={e => setAccount(e.target.value)} />
            </div>
          </div>
          <div className="field">
            <label htmlFor="fund-amount">{t('amount')}</label>
            <div className="input-wrap">
              <input id="fund-amount" value={amount} inputMode="numeric" autoComplete="off" placeholder="0"
                onChange={e => setAmount(e.target.value.replace(/[^\d]/g, ''))} />
            </div>
          </div>
          {me.pin?.set && (
            <div className="field">
              <label htmlFor="fund-pin">{t('pin')}</label>
              <div className="input-wrap">
                <input id="fund-pin" value={pin} type="password" inputMode="numeric" autoComplete="off"
                  onChange={e => setPin(e.target.value)} />
              </div>
            </div>
          )}
        </div>
        <p className="field-hint">{t('fundHint')}</p>
        <button className="submit buy" disabled={mint.isPending || !ready}>{t('mint')}</button>
      </div>
    </form>
  )
}

function ProductForm({ product, onDone }: { product: Product; onDone: () => void }) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [f, setF] = useState({
    code: product.code ?? '', name: product.name ?? '', description: product.description ?? '',
    insurer: product.insurer ?? '', coverage_limit: String(product.coverage_limit ?? ''),
    term_days: String(product.term_days ?? '365'), rate_bps: String(product.rate_bps ?? ''),
    refund: product.refund ?? 'pro_rata', active: product.active ?? true,
    kind: product.kind ?? 'cover', escrow: product.escrow ?? '', release_days: String(product.release_days ?? '10'),
    max_commission_bps: String(product.max_commission_bps ?? '0'), referral_bps: String(product.referral_bps ?? '0'),
  })
  const set = (k: keyof typeof f) => (v: string | boolean) => setF(prev => ({ ...prev, [k]: v }))

  const save = useMutation({
    mutationFn: () => api.defineProduct({
      ...f,
      coverage_limit: Number(f.coverage_limit), term_days: Number(f.term_days),
      // No flat rate: the product is priced by a tariff of its own, or is a fee-less bond.
      rate_bps: f.rate_bps === '' ? undefined : Number(f.rate_bps),
      escrow: f.kind === 'bond' ? f.escrow : undefined,
      release_days: f.kind === 'bond' ? Number(f.release_days) : undefined,
      max_commission_bps: Number(f.max_commission_bps), referral_bps: Number(f.referral_bps),
    }),
    onSuccess: () => {
      message.success(t('saved'))
      qc.invalidateQueries({ queryKey: ['products'] })
      onDone()
    },
    onError: (e: Error) => message.error(e.message),
  })

  const submit = (e: FormEvent) => { e.preventDefault(); save.mutate() }
  const text = (k: keyof typeof f, label: string, extra: Record<string, unknown> = {}) => (
    <div className="field">
      <label htmlFor={k}>{label}</label>
      <div className="input-wrap">
        <input id={k} value={String(f[k])} onChange={e => set(k)(e.target.value)} autoComplete="off" {...extra} />
      </div>
    </div>
  )

  return (
    <form className="panel" onSubmit={submit}>
      <div className="panel-head"><span className="grow">{t('defineProduct')}</span></div>
      <div className="panel-body stack">
        <div className="cards">
          {text('code', t('code'), { placeholder: 'H1', disabled: !!product.code })}
          {text('name', t('name'))}
          {text('insurer', t('insurerAccount'), { placeholder: 'insurer' })}
          {text('coverage_limit', t('coverageLimit'), { inputMode: 'numeric' })}
          {text('term_days', t('termDays'), { inputMode: 'numeric' })}
          {text('rate_bps', t('rateBps'), { inputMode: 'numeric', placeholder: '500' })}
          <div className="field">
            <label htmlFor="kind">{t('kindLabel')}</label>
            <div className="input-wrap">
              <select id="kind" className="select" value={f.kind} disabled={!!product.code}
                onChange={e => set('kind')(e.target.value)}>
                <option value="cover">{t('kindCover')}</option>
                <option value="bond">{t('kindBond')}</option>
              </select>
            </div>
          </div>
          {f.kind === 'bond' && text('escrow', t('escrowAccount'), { placeholder: 'escrow' })}
          {f.kind === 'bond' && text('release_days', t('releaseDays'), { inputMode: 'numeric' })}
          {text('max_commission_bps', t('commissionCap'), { inputMode: 'numeric' })}
          {text('referral_bps', t('referralDefault'), { inputMode: 'numeric' })}
        </div>
        {text('description', t('description'))}
        <div className="row">
          <label className="row" style={{ gap: 6 }}>
            <input type="checkbox" checked={f.active} onChange={e => set('active')(e.target.checked)} />
            {t('onSale')}
          </label>
          <label className="row" style={{ gap: 6 }}>
            <input type="checkbox" checked={f.refund === 'pro_rata'}
              onChange={e => set('refund')(e.target.checked ? 'pro_rata' : 'none')} />
            {t('refundProRata')}
          </label>
        </div>
        <p className="field-hint">{t('productHint')}</p>
        <div className="review-actions">
          <button type="button" className="btn-ghost" onClick={onDone}>{t('cancel')}</button>
          <button className="submit buy" disabled={save.isPending}>{t('save')}</button>
        </div>
      </div>
    </form>
  )
}

/**
 * A product's tariff: method and parameters as JSON, validated by the server
 * before it is saved. Saving makes a new version; quotes name the version they
 * used, so a tariff is never edited underneath a price already given.
 */
function TariffForm({ product, onDone }: { product: Product; onDone: () => void }) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const qc = useQueryClient()
  const versions = useQuery({ queryKey: ['pricing', product.code], queryFn: () => api.pricingVersions(product.code) })
  const live = versions.data?.data.find(v => !v.retired_at)
  const [method, setMethod] = useState<string | null>(null)
  const [params, setParams] = useState<string | null>(null)
  const [note, setNote] = useState('')

  const shownMethod = method ?? live?.method ?? 'factor_table'
  const shownParams = params ?? (live ? JSON.stringify(live.params, null, 2) : EXAMPLE)

  const save = useMutation({
    mutationFn: () => api.setPricing(product.code, { method: shownMethod, params: JSON.parse(shownParams), note }),
    onSuccess: r => {
      message.success(`${t('saved')} · v${r.version}`)
      qc.invalidateQueries({ queryKey: ['pricing', product.code] })
      qc.invalidateQueries({ queryKey: ['products'] })
      onDone()
    },
    onError: (e: Error) => message.error(e.message),
  })

  return (
    <div className="panel">
      <div className="panel-head"><span className="grow">{t('tariff')} · {product.name}</span>
        {live && <span className="chip">v{live.id}</span>}</div>
      <div className="panel-body stack">
        <div className="field">
          <label htmlFor="method">{t('method')}</label>
          <div className="input-wrap">
            <select id="method" className="select" value={shownMethod} onChange={e => setMethod(e.target.value)}>
              <option value="factor_table">factor_table</option>
              <option value="flat_rate">flat_rate</option>
            </select>
          </div>
        </div>
        <div className="field">
          <label htmlFor="params">{t('paramsJson')}</label>
          <textarea id="params" className="code-input" rows={14} value={shownParams}
            onChange={e => setParams(e.target.value)} spellCheck={false} />
        </div>
        <div className="input-wrap">
          <input value={note} placeholder={t('noteWord')} onChange={e => setNote(e.target.value)} />
        </div>
        <div className="review-actions">
          <button type="button" className="btn-ghost" onClick={onDone}>{t('cancel')}</button>
          <button className="submit buy" disabled={save.isPending} onClick={() => {
            try { JSON.parse(shownParams) } catch { message.error('JSON'); return }
            save.mutate()
          }}>{t('saveTariff')}</button>
        </div>
      </div>
    </div>
  )
}

const EXAMPLE = JSON.stringify({
  base: { amount: 100000 },
  factors: [
    { name: 'Vehicle class', fact: 'vehicle_class', values: { A: '0.8', B: '1', C: '1.6' } },
    { name: 'Age', fact: 'age', bands: [{ max: 24, factor: '1.3' }, { min: 25, factor: '1' }] },
  ],
  minimum: 50000, round_to: 100,
}, null, 2)

/** Referral codes: who earns by naming a sale, on which product, at what rate. */
function ReferralCodes({ products }: { products: Product[] }) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const qc = useQueryClient()
  const codes = useQuery({ queryKey: ['referral-codes'], queryFn: api.referralCodes })
  const [code, setCode] = useState('')
  const [account, setAccount] = useState('')
  const [label, setLabel] = useState('')
  const [rateCode, setRateCode] = useState('')
  const [rateProduct, setRateProduct] = useState('')
  const [rate, setRate] = useState('')

  const ok = () => { message.success(t('saved')); qc.invalidateQueries({ queryKey: ['referral-codes'] }) }
  const fail = (e: Error) => message.error(e.message)
  const create = useMutation({ mutationFn: () => api.createReferral({ code, account, label }),
    onSuccess: () => { setCode(''); setAccount(''); setLabel(''); ok() }, onError: fail })
  const setRateM = useMutation({ mutationFn: () => api.setReferralRate(rateCode, { product: rateProduct, rate_bps: Number(rate) }),
    onSuccess: () => { setRate(''); ok() }, onError: fail })
  const disable = useMutation({ mutationFn: (c: string) => api.disableReferral(c), onSuccess: ok, onError: fail })

  const list = codes.data?.data ?? []
  return (
    <div className="panel">
      <div className="panel-head"><span className="grow">{t('referralCodes')}</span></div>
      <div className="panel-body flush">
        {list.length === 0 && <p className="empty">{t('noData')}</p>}
        {list.map(c => (
          <div className="list-row" key={c.code}>
            <div className="grow">
              <div className="title mono">{c.code} → {c.account}</div>
              <div className="sub">{[c.label, ...c.rates.map(r => `${r.product} ${r.rate_bps} bps`)].filter(Boolean).join(' · ')}</div>
            </div>
            {c.disabled_at
              ? <span className="chip">{t('disabledWord')}</span>
              : <button className="btn-ghost" onClick={() => disable.mutate(c.code)}>{t('disable')}</button>}
          </div>
        ))}
      </div>
      <div className="panel-body stack">
        <div className="cards">
          <div className="field"><label htmlFor="rc-code">{t('newCode')}</label>
            <div className="input-wrap"><input id="rc-code" value={code} onChange={e => setCode(e.target.value.toUpperCase())} /></div></div>
          <div className="field"><label htmlFor="rc-acct">{t('referrerAccount')}</label>
            <div className="input-wrap"><input id="rc-acct" value={account} onChange={e => setAccount(e.target.value)} /></div></div>
          <div className="field figure wide"><label htmlFor="rc-label">{t('labelWord')}</label>
            <div className="input-wrap"><input id="rc-label" value={label} onChange={e => setLabel(e.target.value)} /></div></div>
        </div>
        <button className="btn-small" disabled={!code || !account || create.isPending} onClick={() => create.mutate()}>{t('newCode')}</button>
        <div className="cards">
          <div className="field"><label htmlFor="rr-code">{t('referralCode')}</label>
            <div className="input-wrap">
              <select id="rr-code" className="select" value={rateCode} onChange={e => setRateCode(e.target.value)}>
                <option value="" />{list.filter(c => !c.disabled_at).map(c => <option key={c.code}>{c.code}</option>)}
              </select></div></div>
          <div className="field"><label htmlFor="rr-prod">{t('product')}</label>
            <div className="input-wrap">
              <select id="rr-prod" className="select" value={rateProduct} onChange={e => setRateProduct(e.target.value)}>
                <option value="" />{products.map(p => <option key={p.code} value={p.code}>{p.name}</option>)}
              </select></div></div>
          <div className="field"><label htmlFor="rr-rate">{t('rateFor')} (bps)</label>
            <div className="input-wrap"><input id="rr-rate" inputMode="numeric" value={rate}
              onChange={e => setRate(e.target.value.replace(/[^\d]/g, ''))} /></div></div>
        </div>
        <button className="btn-small" disabled={!rateCode || !rateProduct || rate === '' || setRateM.isPending}
          onClick={() => setRateM.mutate()}>{t('setRate')}</button>
      </div>
    </div>
  )
}
